/**
 * A CMMD project's board, opened on its Settings tab.
 *
 * This is what "Project settings" means, and the only thing it can mean: a
 * Forge execution project has no page of its own. Status, priority, lead,
 * target date, task key, repository and team all live on the board.
 *
 * `cmmdProjectId` is a number everywhere it is produced and a string in the
 * route, so the conversion happens here rather than at each call site.
 */
export function resolveCmmdProjectSettingsRoute(cmmdProjectId: number) {
  return {
    to: "/project/$projectKey" as const,
    params: { projectKey: String(cmmdProjectId) },
    search: { tab: "settings" as const },
  };
}

/** The subset of `getProjectLink`'s response this decision reads. */
export interface ProjectSettingsCmmdLink {
  readonly linked: boolean;
  readonly cmmdProjectId?: number | undefined;
}

export interface ProjectSettingsCmmdProjectInput {
  readonly environmentId: string;
  readonly id: string;
  readonly cwd: string;
  /**
   * Present when the row already knows its CMMD project — cloud rows carry it
   * on their catalog metadata. Absent for a local checkout, whose link is
   * keyed by `cwd` and only knowable through a read.
   */
  readonly cmmdProjectId?: number | null | undefined;
}

/**
 * Which CMMD project the gear should open, or `null` for "there isn't one".
 *
 * Resolved on click rather than on render on purpose. The cwd link is a fetch,
 * and the picker lists every project, so resolving eagerly would mean one
 * request per project every time the sidebar mounts to serve a click that
 * usually never comes.
 *
 * A failed read answers `null` rather than rejecting: the caller's fallback is
 * the workspace Settings tab, and a gear that opens the slightly-wrong page
 * beats a gear that does nothing when CMMD is unreachable.
 */
export async function resolveProjectSettingsCmmdProjectId(input: {
  readonly project: ProjectSettingsCmmdProjectInput;
  readonly loadCmmdProjectLink: (params: {
    readonly environmentId: string;
    readonly projectId: string;
    readonly cwd: string;
  }) => Promise<ProjectSettingsCmmdLink>;
}): Promise<number | null> {
  const { project } = input;
  if (typeof project.cmmdProjectId === "number") return project.cmmdProjectId;
  // No workspace root means nothing to look the link up by — a cloud row that
  // did not carry an id has already answered above.
  if (!project.cwd) return null;
  try {
    const link = await input.loadCmmdProjectLink({
      environmentId: project.environmentId,
      projectId: project.id,
      cwd: project.cwd,
    });
    return link.linked && typeof link.cmmdProjectId === "number" ? link.cmmdProjectId : null;
  } catch {
    return null;
  }
}
